<template>
    <div class="flex flex-col min-h-screen"
         v-bind:style="{ '--banner-offset': bannerStore.visible ? '2.25rem' : '0px' }"
    >
        <app-banner />

        <app-bar />

        <router-view v-if="localizationStore.strings" />

        <div class="bg-white cursor-progress fixed h-screen left-0 opacity-50 top-0 w-screen z-40"
             v-if="loading"
        ></div>
        <div class="fixed left-0 loading overflow-hidden top-0 w-screen z-50" v-show="loading">
            <div class="animate-loader relative bg-success h-2 left-0 opacity-100 top-0 -translate-x-full w-full"></div>
        </div>

        <app-footer></app-footer>

        <div class="bottom-20 duration-1000 fixed left-2 max-w-[20rem] pointer-events-none right-auto z-50"
             v-bind:class="{
                'opacity-0' : hideAlerts
             }"
             v-if="alerts.length > 0"
        >
            <div class="flex items-center mt-2 pointer-events-auto px-3 py-2 rounded-lg shadow-md text-sm transition-all"
                 v-bind:class="{
                    'bg-error text-red-900' : alert.type === 'error',
                    'bg-info text-sky-800' : alert.type === 'info',
                    'bg-success text-emerald-800' : alert.type === 'success',
                    'bg-warning text-amber-700' : alert.type === 'warning',
                 }"
                 v-for="(alert, index) in alerts"
            >
                <c-icon class="h-5 mr-2 shrink-0 stroke-red-900 w-5" icon="meh" v-if="alert.type === 'error'" />
                <c-icon class="h-5 mr-2 shrink-0 stroke-sky-700 w-5" icon="info" v-if="alert.type === 'info'" />
                <c-icon class="h-5 mr-2 shrink-0 stroke-emerald-700 w-5" icon="check" v-if="alert.type === 'success'" />
                <c-icon class="h-5 mr-2 shrink-0 stroke-amber-700 w-5" icon="alert-triangle" v-if="alert.type === 'warning'" />

                <div>
                    <strong class="block">{{ alert.title }}</strong>
                    <p>{{ alert.message }}</p>
                </div>

                <button class="ml-2 shrink-0" type="button" v-on:click="alerts.splice(index, 1)">
                    <c-icon class="h-4 w-4"
                            v-bind:class="{
                                'stroke-red-900' : alert.type === 'error',
                                'stroke-sky-700' : alert.type === 'info',
                                'stroke-emerald-700' : alert.type === 'success',
                                'stroke-amber-700' : alert.type === 'warning',
                            }"
                            icon="x"
                    />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { onMounted, ref, watch } from 'vue';
    import {  useRouter } from 'vue-router'
    import { CIcon } from '@teamfurther/cinderblock';
    import '@vuepic/vue-datepicker/dist/main.css';
    import AppBar from './components/AppBar.vue';
    import AppBanner from './components/AppBanner.vue';
    import AppFooter from './components/AppFooter.vue';
    import { useBannerStore } from './stores/banner';
    import { useLocalizationStore } from './stores/localization';
    import { useRequestStore } from './stores/request';
    import { useSessionStore } from './stores/session';

    const alerts = ref<object[]>([]);
    const alertTimeout = ref();
    const bannerStore = useBannerStore();
    const hideAlerts = ref<Boolean>(false);
    const loading = ref<Boolean>(false);
    const localizationStore = useLocalizationStore();
    const requestStore = useRequestStore();
    const router = useRouter();
    const sessionStore = useSessionStore();

    router.beforeEach((to, from) => {
        const sessionStore = useSessionStore();

        sessionStore.getUserFromLocalStorage();

        if (!sessionStore.user && to.meta.requiresAuth) {
            router.push({ name : 'login' })
        }
    });

    onMounted(async () => {
        alertTimeout.value = setTimeout(() => {
            hideAlerts.value = true;

            alerts.value.shift();
        }, 8000);
        
        loading.value = true;

        await localizationStore.getStrings();

        loading.value = false;

        sessionStore.getUserFromLocalStorage();

        if (sessionStore.user) {
            bannerStore.fetch();
        }
    });

    watch(() => alerts.value.length, () => {
        clearTimeout(alertTimeout.value);
        hideAlerts.value = false;

        alertTimeout.value = setTimeout(() => {
            hideAlerts.value = true;

            alerts.value.shift();
        }, 8000);
    });

    watch(() => requestStore.loading, () => {
        loading.value = requestStore.loading > 0;
    });

    watch(() => requestStore.status, () => {
        if (requestStore.status) {
            alerts.value.push({
                message: requestStore.message,
                title: requestStore.title,
                type: requestStore.status === 200 ? 'success' : 'error'
            });
        }
    });

    watch(() => sessionStore.user, (user) => {
        if (user) {
            bannerStore.fetch();
        }
    });
</script>
